import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, LinearProgress } from '@mui/material';
import styled from 'styled-components';
import { useSnackbar } from 'notistack';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useCreatePushMutation, useFetchPushsQuery } from '@components/Push/gql/push.generated';
import PageContainer from '@components/UI/PageContainer/PageContainer';
import { format } from 'date-fns';

const Title = styled(Typography)`
  grid-area: title;
  font-weight: 500;
  line-height: 1.2;
  color: #222;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 100%;
  margin: 0 auto;
  padding-bottom: 20px;
`;

const TextFieldContainer = styled.div`
  margin-bottom: 16px;
  width: 100%;
`;

const Plashka = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
  border: 1px solid #ccc;
  width: 100%;
`;

const ItemWrapper = styled.div`
  font-size: 14px;
  padding-left: 12px;
  line-height: 1.2;
  margin-bottom: 10px;
  border-bottom: 1px solid #dfdfdf;
  display: flex;
  justify-content: space-between;
`;

const ItemContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemInfo = styled.div`
  display: flex;
`;

const ScrollContainer = styled.div`
  height: 400px;
  overflow-y: auto;
  padding-right: 15px;
  position: relative;
  overscroll-behavior: contain;
  isolation: isolate;
`;

const CharCounter = styled(Typography) <{ isError: boolean }>`
  font-size: 12px;
  color: ${({ isError }) => (isError ? '#d32f2f' : '#666')};
  margin-top: 4px;
`;

const Pushes: React.FC = () => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [url, setUrl] = useState('');
    const [titleLength, setTitleLength] = useState(0);
    const [contentLength, setContentLength] = useState(0);
    const [titleError, setTitleError] = useState(false);
    const [contentError, setContentError] = useState(false);
    const [pushes, setPushes] = useState<any[]>([]);
    const [after, setAfter] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(true);
    const { enqueueSnackbar } = useSnackbar();
    const [createPush, { loading: createLoading }] = useCreatePushMutation();
    const { data, loading, error, fetchMore, refetch } = useFetchPushsQuery({
        variables: {
            limit: 15,
            sort: 'CREATED_AT_DESC',
        },
        notifyOnNetworkStatusChange: true,
    });

    // Валидация URL
    const isValidUrl = (url: string) => {
        const publicationRegexp1 = /https?:\/\/.*ura\.?news\/(news|articles)\/\d{1,10}\/?/;
        const publicationRegexp2 = /https?:\/\/.*ura\.?news\/(news|articles)\//;
        return publicationRegexp1.test(url) || publicationRegexp2.test(url);
    };

    // Обработка начальной загрузки данных и refetch
    useEffect(() => {
        if (data?.pushs?.edges) {
            const newPushes = data.pushs.edges
                .map(edge => edge?.node)
                .filter(node => node && node.id);
            setPushes(newPushes);
            setAfter(data.pushs.pageInfo?.endCursor ?? null);
            setHasMore(data.pushs.pageInfo?.hasNextPage ?? false);
        } else {
            setPushes([]);
            setHasMore(false);
        }
    }, [data]);

    // Обработка ошибок
    useEffect(() => {
        if (error) {
            console.log('Error:', error);
            enqueueSnackbar(`Ошибка загрузки данных: ${error.message}`, {
                variant: 'error',
                autoHideDuration: 5000,
            });
            setHasMore(false);
        }
    }, [error, enqueueSnackbar]);

    // Загрузка следующей страницы
    const fetchMoreData = async () => {
        if (!hasMore || loading) {
            return;
        }

        try {
            const { data: newData } = await fetchMore({
                variables: {
                    first: 15,
                    after,
                    sort: 'CREATED_AT_DESC',
                },
                updateQuery: (prev, { fetchMoreResult }) => {
                    if (!fetchMoreResult?.pushs?.edges || fetchMoreResult.pushs.edges.length === 0) {
                        setHasMore(false);
                        return prev;
                    }

                    const prevIds = new Set(prev.pushs?.edges.map(edge => edge.node.id));
                    const newEdges = fetchMoreResult.pushs.edges.filter(edge => !prevIds.has(edge.node.id));

                    if (newEdges.length === 0) {
                        setHasMore(false);
                        return prev;
                    }

                    return {
                        pushs: {
                            __typename: prev.pushs?.__typename,
                            edges: [...prev.pushs?.edges, ...newEdges],
                            pageInfo: fetchMoreResult.pushs.pageInfo,
                        },
                    };
                },
            });

            const newEdges = newData.pushs?.edges || [];
            const newPushes = newEdges
                .map(edge => edge?.node)
                .filter(node => node && node.id);
            setPushes(prev => [...prev, ...newPushes]);
            setAfter(newData.pushs?.pageInfo?.endCursor ?? null);
            setHasMore(newData.pushs?.pageInfo?.hasNextPage ?? false);
        } catch (err) {
            console.log('FetchMore Error:', err);
            enqueueSnackbar(`Ошибка загрузки данных: ${(err as Error).message}`, {
                variant: 'error',
                autoHideDuration: 5000,
            });
            setHasMore(false);
        }
    };

    // Отправка push-уведомления
    const handleCreatePush = async () => {
        if (!title || !content || !url) {
            enqueueSnackbar('Заполните все поля: заголовок, описание и ссылку', {
                variant: 'info',
                autoHideDuration: 5000,
            });
            return;
        }

        if (title.length > 50) {
            setTitleError(true);
            enqueueSnackbar('Превышен лимит в 50 символов для заголовка', {
                variant: 'error',
                autoHideDuration: 5000,
            });
            return;
        }

        if (content.length > 120) {
            setContentError(true);
            enqueueSnackbar('Превышен лимит в 120 символов для описания', {
                variant: 'error',
                autoHideDuration: 5000,
            });
            return;
        }

        if (!isValidUrl(url)) {
            enqueueSnackbar('Введите корректную ссылку на публикацию', {
                variant: 'error',
                autoHideDuration: 5000,
            });
            return;
        }

        try {
            const { data } = await createPush({
                variables: {
                    data: {
                        title,
                        content,
                        url,
                    },
                },
            });

            if (data?.createPush?.status) {
                enqueueSnackbar('Push-уведомление успешно отправлено', {
                    variant: 'success',
                    autoHideDuration: 5000,
                });
                setTitle('');
                setContent('');
                setUrl('');
                setTitleLength(0);
                setContentLength(0);
                setTitleError(false);
                setContentError(false);
                setPushes([]);
                setAfter(null);
                setHasMore(true);
                await refetch({ limit: 15, sort: 'CREATED_AT_DESC' });
            } else {
                enqueueSnackbar('Не удалось отправить push-уведомление', {
                    variant: 'error',
                    autoHideDuration: 5000,
                });
            }
        } catch (error) {
            enqueueSnackbar(`Ошибка: ${(error as Error).message}`, {
                variant: 'error',
                autoHideDuration: 5000,
            });
        }
    };

    // Форматирование даты
    const formatDate = (isoDate: string) => {
        try {
            return format(new Date(isoDate), 'dd.MM.yyyy HH:mm');
        } catch (error) {
            console.error('Date format error:', error);
            return isoDate;
        }
    };

    // Отладка прокрутки
    useEffect(() => {
        const scrollContainer = document.getElementById('scroll-container');
        if (scrollContainer) {
            const handleScroll = () => {
                const scrollTop = scrollContainer.scrollTop;
                const scrollHeight = scrollContainer.scrollHeight;
                const clientHeight = scrollContainer.clientHeight;
                const threshold = (scrollHeight - clientHeight) * 0.5;
            };
            scrollContainer.addEventListener('scroll', handleScroll);
            return () => scrollContainer.removeEventListener('scroll', handleScroll);
        }
    }, [hasMore]);

    return (
        <PageContainer>
            <FormContainer>
                <Plashka>
                    <TextFieldContainer>
                        <TextField
                            label="Заголовок уведомления"
                            variant="outlined"
                            value={title}
                            onChange={e => {
                                const value = e.target.value;
                                setTitle(value);
                                setTitleLength(value.length);
                                setTitleError(value.length > 50);
                            }}
                            error={titleError}
                            helperText={titleError ? 'Превышен лимит в 50 символов' : ''}
                            style={{ width: '100%' }}
                            InputProps={{
                                style: {
                                    height: '45px',
                                    padding: '0 14px',
                                },
                            }}
                            disabled={createLoading}
                        />
                        <CharCounter isError={titleLength > 50}>
                            {titleLength}/50
                        </CharCounter>
                    </TextFieldContainer>
                    <TextFieldContainer>
                        <TextField
                            label="Текст уведомления"
                            variant="outlined"
                            value={content}
                            onChange={e => {
                                const value = e.target.value;
                                setContent(value);
                                setContentLength(value.length);
                                setContentError(value.length > 120);
                            }}
                            error={contentError}
                            helperText={contentError ? 'Превышен лимит в 120 символов' : ''}
                            style={{ width: '100%' }}
                            InputProps={{
                                style: {
                                    height: '45px',
                                    padding: '0 14px',
                                },
                            }}
                            disabled={createLoading}
                        />
                        <CharCounter isError={contentLength > 120}>
                            {contentLength}/120
                        </CharCounter>
                    </TextFieldContainer>
                    <TextFieldContainer>
                        <TextField
                            label="Ссылка на новость/статью"
                            variant="outlined"
                            value={url}
                            onChange={e => setUrl(e.target.value)}
                            style={{ width: '100%' }}
                            InputProps={{
                                style: {
                                    height: '45px',
                                    padding: '0 14px',
                                },
                            }}
                            disabled={createLoading}
                        />
                    </TextFieldContainer>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCreatePush}
                        disabled={createLoading}
                    >
                        {createLoading ? 'Отправка...' : 'Отправить push'}
                    </Button>
                </Plashka>
            </FormContainer>
            <ScrollContainer id="scroll-container">
                <InfiniteScroll
                    dataLength={pushes.length}
                    next={fetchMoreData}
                    hasMore={hasMore}
                    loader={<LinearProgress color="primary" />}
                    scrollThreshold={0.8}
                    scrollableTarget="scroll-container"
                >
                    <Box paddingRight="15px" minHeight="600px">
                        {pushes.length === 0 && !loading && (
                            <Typography variant="body2">Нет пушей для отображения.</Typography>
                        )}
                        {pushes.map(push => (
                            <ItemWrapper key={push.id}>
                                <ItemInfo>
                                    <ItemContentWrapper>
                                        <Title>
                                            <a href={push.link} title="Перейти по ссылке">
                                                {push.title}
                                            </a>
                                        </Title>
                                        <Typography color="secondary" component="span" variant="subtitle1">
                                            {push.content}
                                        </Typography>
                                        <Typography color="textSecondary" component="div" variant="caption">
                                            {formatDate(push.createdAt)}
                                        </Typography>
                                    </ItemContentWrapper>
                                </ItemInfo>
                            </ItemWrapper>
                        ))}
                    </Box>
                </InfiniteScroll>
            </ScrollContainer>
        </PageContainer>
    );
};

export default Pushes;